// Batch 134 — hand-converted from src/legal/privacy.md, which is the SOURCE OF
// RECORD. Edit the markdown first, then mirror it here.
//
// legalPages.test.js reads privacy.md, extracts every heading, and asserts each
// one appears in what this renders. So a SECTION added to the lawyer's copy and
// forgotten here goes red. A REWORDED SENTENCE inside a section does not, and
// that limit is stated in the test rather than left to be discovered.
import LegalPage, { H2, H3, P, B, UL, LI, Rule, Table, Mail } from "./LegalPage";

export default function PrivacyPolicy() {
  return (
    <LegalPage title="SparkCue Privacy Policy" testId="legal-privacy">
      <P>
        SparkCue is operated by an individual based in Ontario, Canada. That person is also
        the privacy contact and is accountable for how SparkCue handles your information.
        Contact: <B><Mail /></B>
      </P>
      <P>
        This policy explains what SparkCue collects, where it goes, how long it stays, and
        what you can ask us to do about it. It is written to be read, not to be survived.
      </P>
      <Rule />

      <H2>The short version</H2>
      <P>Your conversations stay on your device. We do not store them.</P>
      <P>
        To generate a reply, your conversation is sent to an AI provider and comes back. It
        is not kept.
      </P>
      <P>We keep the replies we generate for one hour so we can deliver them to you.</P>
      <P>
        If you tap Helpful, Not helpful, or copy a reply, we keep <B>that one reply</B> so we
        can find out which coaching lands and which misses. We keep those for 12 months.
      </P>
      <P>We keep your email address so you have an account.</P>
      <Rule />

      <H2>What we collect, and why</H2>

      <H3>Your conversations</H3>
      <P>
        When you paste a conversation or upload a screenshot, that text is held{" "}
        <B>on your device</B> — in your browser's local storage — and sent to our server only
        for as long as it takes to generate a reply. We do not store it.
      </P>
      <P>
        Your saved conversations, your history, and everything you have told SparkCue about
        yourself live on your device. Clearing your browser data deletes them, and we cannot
        recover them.
      </P>

      <H3>The replies we generate</H3>
      <P>
        The replies SparkCue writes for you are held on our server for <B>one hour</B>, so
        that we can deliver them to you and recover them if your connection drops. After an
        hour they are deleted automatically.
      </P>
      <P>
        <B>One exception.</B> If you tap Helpful, Not helpful, or copy a reply, we keep that
        reply. It is how we find out which coaching works. That record includes the reply
        text, which direction you were on, and where the conversation was — not your
        conversation. We keep it for 12 months, then delete it.
      </P>
      <P>If you type something into the feedback box, we keep that too.</P>

      <H3>Your account</H3>
      <UL>
        <LI><B>Your email address</B>, so you have an account and can sign in again.</LI>
        <LI>
          <B>A record of each device you sign in on</B> — the type of device and roughly when
          it was last used. We store only a one-way hash of your sign-in token, never the
          token itself. Our server records the address its own hosting provider passes along,
          which in practice is the provider's, not yours.
        </LI>
        <LI><B>How many replies you have generated</B>, so we can apply the free tier.</LI>
        <LI><B>One session cookie</B>, to keep you signed in. It carries no personal information.</LI>
      </UL>

      <H3>What we do not collect</H3>
      <P>
        We do not ask for your name, your age, your location, your photographs, or anything
        about the person you are texting. We do not use advertising trackers. We do not sell
        anything to anyone.
      </P>
      <Rule />

      <H2>Who else touches your data</H2>
      <P>SparkCue is built on other people's services. Each of them holds a part of it.</P>
      <Table
        head={["Who", "What they hold"]}
        rows={[
          [<B key="a">Emergent Labs Inc. (United States)</B>,
           "Routes your conversation to the AI model that writes your reply. Our account is restricted to Anthropic only."],
          [<B key="b">MongoDB Atlas</B>,
           "Your account, your email, your device records, your usage count, and your feedback."],
          [<B key="c">Railway</B>, "Runs our server. Holds server logs."],
          [<B key="d">Vercel</B>, "Serves the app to your browser. Holds access logs."],
          [<B key="e">Resend</B>, "Your email address, to send you a sign-in code."],
        ]}
      />
      <P>
        <B>On the AI provider.</B> Your conversation is sent to Emergent, which routes it to
        an AI model and sends the reply back.
      </P>
      <P>
        Emergent can route to several AI companies.{" "}
        <B>We have restricted our account so that yours only ever goes to Anthropic</B>, the
        company behind Claude.
      </P>
      <P>
        <B>We do not use your conversations to train AI models.</B> Emergent have confirmed in
        writing that they do not use your data to train general AI models without explicit
        consent.
      </P>
      <P>
        We have asked Emergent for their retention terms and for a copy of their data
        processing agreement, and have not yet received them. When we do, we will update this
        policy.
      </P>
      <P>If our understanding of any provider's handling changes, we will update this policy.</P>
      <Rule />

      <H2>How long we keep things</H2>
      <Table
        head={["What", "How long"]}
        rows={[
          ["Your conversations", "Not stored by us. They live on your device until you delete them."],
          ["Generated replies", "One hour, then deleted automatically."],
          ["A reply you gave feedback on or copied", "12 months."],
          ["Text you type into the feedback box", "12 months."],
          ["Your email address and account", "Until you ask us to delete it."],
          ["Device records", "Until you sign that device out, or you delete your account."],
          ["Sign-in codes", "Fifteen minutes."],
        ]}
      />
      <Rule />

      <H2>Your rights</H2>
      <P>You can ask us to:</P>
      <UL>
        <LI><B>Tell you</B> what we hold about you</LI>
        <LI><B>Send you a copy</B> of it</LI>
        <LI><B>Correct</B> anything that is wrong</LI>
        <LI><B>Delete</B> your account and everything attached to it</LI>
        <LI><B>Object to</B> or <B>restrict</B> how we use it</LI>
      </UL>
      <P>Email <B><Mail /></B>. We will respond within 30 days.</P>
      <P>
        Deletion is currently done by hand. When you ask, we delete your account, your email
        address, your device records, your usage history, and your feedback records.
        Conversations on your own device are yours to delete — clearing your browser data
        removes them.
      </P>
      <P>
        If you are in the UK or EU, you also have the right to complain to your data
        protection authority. In Canada, you can complain to the Office of the Privacy
        Commissioner.
      </P>
      <Rule />

      <H2>Why we are allowed to hold it</H2>
      <P>Where the UK or EU GDPR applies:</P>
      <UL>
        <LI>
          <B>Your email, device records, and usage count</B> — to provide the service you
          asked for.
        </LI>
        <LI>
          <B>Feedback you give us</B> — our legitimate interest in making the coaching better.
        </LI>
        <LI>
          <B>Server and access logs</B> — our legitimate interest in keeping the service
          running and secure.
        </LI>
      </UL>
      <P>Where Canadian law applies, we rely on your consent, given when you create an account.</P>
      <Rule />

      <H2>Security</H2>
      <P>
        Sign-in tokens are stored only as a one-way hash, so we could not read yours even if
        we wanted to. Everything travels over an encrypted connection. Access to the database
        is limited to the operator.
      </P>
      <P>
        No system is perfectly secure. If a breach occurs that triggers notification
        obligations under applicable law, we will notify affected individuals and the relevant
        regulator as required.
      </P>
      <Rule />

      <H2>Children</H2>
      <P>
        SparkCue is for adults. You must be 18 or older to use it. We do not knowingly collect
        information from anyone under 18, and if we learn we have, we will delete it.
      </P>
      <Rule />

      <H2>Where your data goes</H2>
      <P>
        Our providers are in the United States and elsewhere. Using SparkCue means your data
        crosses borders.
      </P>
      <Rule />

      <H2>Changes</H2>
      <P>
        If we change this policy in a way that matters, we will update the date above and post
        the new version. Material changes will be flagged in the app.
      </P>
      <Rule />

      <H2>Contact</H2>
      <P><B><Mail /></B></P>
      <P>
        SparkCue is operated by an individual based in Ontario, Canada. If you need a postal
        address for a formal request, ask and we will provide one.
      </P>
    </LegalPage>
  );
}
